import type { Stance } from "@hammer/core";
import { formatMoney, parseMoney } from "@hammer/core/money";
import { useEffect, useRef, useState } from "react";
import { absoluteTime, StanceControl } from "../components/lot-bits.tsx";
import { api, ApiError, type CaptureResponse, type LotPage } from "../lib/api.ts";

interface Payload {
  url: string;
  json?: string;
  html?: string;
}

type State =
  | { s: "waiting" }
  | { s: "working" }
  | { s: "login"; payload: Payload; error?: string }
  | { s: "saved"; created: boolean; lot: LotPage; issues: { detail: string }[] }
  | { s: "error"; message: string }
  | { s: "nothing" };

/** The bookmarklet gzips the lot and puts it in the URL fragment (#v=2&d=…), which never leaves the browser. */
async function readFragment(hash: string): Promise<Payload | null> {
  const d = new URLSearchParams(hash.replace(/^#/, "")).get("d");
  if (!d) return null;
  const b64 = d.replace(/-/g, "+").replace(/_/g, "/");
  const bytes = Uint8Array.from(atob(b64 + "===".slice((b64.length + 3) % 4)), (c) => c.charCodeAt(0));
  const text = await new Response(new Blob([bytes]).stream().pipeThrough(new DecompressionStream("gzip"))).text();
  return JSON.parse(text) as Payload;
}

/**
 * The popup the bookmarklet opens: saves the lot, then lets you decide on it right here.
 */
export function Capture() {
  const [state, setState] = useState<State>({ s: "waiting" });
  const [legacy, setLegacy] = useState(false);
  const started = useRef(false);

  async function save(payload: Payload) {
    setState({ s: "working" });
    try {
      const r = await api<CaptureResponse>("/capture", { method: "POST", body: { ...payload, save: true } });
      if (r.status === "needs_client") throw new Error("The auction site didn't hand over the lot's details. Reload the lot page and click the bookmark again.");
      if (r.status === "error") throw new Error(r.message);
      if (r.status !== "saved") throw new Error("Unexpected answer from Hammer.");
      const lot = await api<LotPage>(`/lots/${r.lotId}`);
      setState({ s: "saved", created: r.created, lot, issues: r.issues });
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) return setState({ s: "login", payload });
      setState({ s: "error", message: (e as Error).message });
    }
  }

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    readFragment(location.hash)
      .then((p) => {
        history.replaceState(null, "", "/capture"); // don't resubmit on reload
        if (p) return save(p);
        // An older bookmarklet sends the page by postMessage instead.
        const onMessage = (e: MessageEvent) => {
          if (e.source !== window.opener || e.data?.type !== "hammer-capture" || typeof e.data.url !== "string") return;
          (window.opener as Window).postMessage("hammer-got-it", e.origin);
          window.removeEventListener("message", onMessage);
          setLegacy(true);
          void save({ url: e.data.url, html: e.data.html });
        };
        window.addEventListener("message", onMessage);
        setTimeout(() => setState((s) => (s.s === "waiting" ? { s: "nothing" } : s)), 4000);
      })
      .catch(() => setState({ s: "error", message: "The lot data from the bookmark was damaged. Click the bookmark again." }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-dvh p-6 grid content-start gap-5 max-w-[30rem] mx-auto">
      <span className="wordmark">Hammer</span>
      {(state.s === "waiting" || state.s === "working") && <p className="muted" role="status">Saving the lot…</p>}
      {state.s === "nothing" && (
        <div className="grid gap-2">
          <h1 className="heading text-[21.6px]">Nothing arrived from the lot page</h1>
          <p>The bookmarklet was updated. Open Hammer, go to Sources, and drag the new "Add to Hammer" button to your bookmarks bar in place of the old one.</p>
        </div>
      )}
      {state.s === "error" && <p className="text-red" role="alert">{state.message}</p>}
      {state.s === "login" && <InlineLogin error={state.error} onDone={() => void save(state.payload)} onError={(error) => setState({ ...state, error })} />}
      {state.s === "saved" && <Saved {...state} />}
      {legacy && state.s === "saved" && <p className="text-[13.5px] muted">This came from an older version of the bookmarklet, which doesn't work on every site. Replace it from Hammer's Sources page.</p>}
    </div>
  );
}

const AUTO_CLOSE_S = 8;

const toLocalInput = (iso: string) => {
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
};

function Saved({ created, lot: initial, issues }: { created: boolean; lot: LotPage; issues: { detail: string }[] }) {
  const [lot, setLot] = useState(initial);
  const [status, setStatus] = useState<string | null>(null);
  // Close by itself once the update is saved, unless you start using the popup or there's something to check.
  const [closeIn, setCloseIn] = useState<number | null>(issues.length ? null : AUTO_CLOSE_S);
  useEffect(() => {
    if (closeIn == null) return;
    if (closeIn <= 0) return void window.close();
    const t = setTimeout(() => setCloseIn((n) => (n == null ? null : n - 1)), 1000);
    return () => clearTimeout(t);
  }, [closeIn]);
  useEffect(() => {
    const stop = () => setCloseIn(null);
    window.addEventListener("pointerdown", stop);
    window.addEventListener("keydown", stop);
    return () => {
      window.removeEventListener("pointerdown", stop);
      window.removeEventListener("keydown", stop);
    };
  }, []);

  // Every edit saves on its own; closing waits for any save still in flight.
  const pending = useRef(new Set<Promise<unknown>>());
  const save = (p: Promise<unknown>, what: string) => {
    pending.current.add(p);
    setStatus("Saving…");
    p.then(() => setStatus(`${what} saved`), (e) => setStatus(`Couldn't save ${what.toLowerCase()}: ${(e as Error).message}`)).finally(() => pending.current.delete(p));
    return p;
  };
  const closeWhenSaved = async () => {
    await Promise.allSettled([...pending.current]);
    window.close();
  };
  const refresh = async () => setLot(await api<LotPage>(`/lots/${lot.id}`));
  const patch = (body: Record<string, unknown>, what: string) => save(api(`/lots/${lot.id}`, { method: "PATCH", body }).then(refresh), what);

  const [max, setMax] = useState(lot.stance.maxBidCents != null ? String(lot.stance.maxBidCents / 100) : "");
  const now = Date.now();
  const setStance = (stance: Stance, extra: { maxBidCents?: number | null; note?: string } = {}) => {
    const prev = lot;
    setLot({ ...lot, stance: { ...lot.stance, stance, ...extra } });
    return save(
      api(`/lots/${lot.id}/stance`, { method: "PUT", body: { stance, ...extra } }).catch((e) => {
        setLot(prev);
        throw e;
      }),
      extra.note !== undefined ? "Note" : extra.maxBidCents !== undefined ? "Max bid" : "Choice",
    );
  };
  const bid = lot.latest?.currentBidCents ?? null;
  const maxCents = max.trim() ? parseMoney(max) : null;
  return (
    <div className="grid gap-4">
      <p className="text-[14px] muted flex justify-between gap-3" role="status">
        <span>{created ? "Added to your list" : "Updated: already on your list"}</span>
        {closeIn != null && <button className="underline underline-offset-2 bg-transparent border-0 p-0 muted text-[14px]" onClick={() => setCloseIn(null)}>Closing in {closeIn}s, keep open</button>}
      </p>
      <div className="flex gap-3">
        {lot.images[0] && <img src={lot.images[0]} alt="" className="w-24 h-18 object-cover rounded shrink-0" referrerPolicy="no-referrer" />}
        <div className="min-w-0">
          <h1 className="font-[600] text-[17px] leading-snug">{lot.title}</h1>
          <p className="text-[13.5px] muted">{[lot.locationText, lot.lotNumber && `lot ${lot.lotNumber}`].filter(Boolean).join(", ")}</p>
        </div>
      </div>
      <div className="flex items-baseline justify-between gap-4">
        <span className="board text-[26px]">{bid != null ? formatMoney(bid) : "No bids"}</span>
        {lot.latest?.endAt && <span className="muted text-[14px]">ends {absoluteTime(lot.latest.endAt, now)}</span>}
      </div>
      {issues.length > 0 && <ul className="text-amber text-[14px] list-disc pl-5">{issues.map((i) => <li key={i.detail}>{i.detail}</li>)}</ul>}
      <div className="grid gap-3">
        <StanceControl value={lot.stance.stance} onChange={(s) => void setStance(s)} />
        {lot.stance.stance === "bidding" && (
          <label className="grid">
            <span className="label">Your max bid</span>
            <input className="field num" inputMode="decimal" placeholder="$0" value={max} onChange={(e) => setMax(e.target.value)}
              onBlur={() => maxCents !== lot.stance.maxBidCents && (max.trim() === "" || maxCents != null) && void setStance("bidding", { maxBidCents: maxCents })} />
          </label>
        )}
        <TextDetail label="Notes" multiline placeholder="Condition, mileage, who to call, what it needs" value={lot.stance.note} onSave={(note) => setStance(lot.stance.stance, { note })} />
      </div>

      <details className="text-[14px]" open={issues.length > 0}>
        <summary className="cursor-pointer muted">Correct or add details</summary>
        <div className="grid gap-3 mt-3">
          <TextDetail label="Title" value={lot.title} onSave={(title) => title.trim() && patch({ title: title.trim() }, "Title")} />
          <div className="grid grid-cols-2 gap-3">
            <TextDetail label="Current bid" numeric placeholder="$0" value={bid != null ? String(bid / 100) : ""}
              onSave={(v) => { const c = v.trim() ? parseMoney(v) : null; if (v.trim() && c == null) return setStatus("Enter the bid as a number, like 3800"); return patch({ currentBidCents: c }, "Current bid"); }} />
            <TextDetail label="Buyer's premium, %" numeric placeholder="15" value={lot.buyerPremiumBps != null ? String(lot.buyerPremiumBps / 100) : ""}
              onSave={(v) => { const n = Number(v); if (v.trim() === "" || !Number.isFinite(n)) return; return patch({ buyerPremiumBps: Math.round(n * 100) }, "Premium"); }} />
          </div>
          <label className="grid">
            <span className="label">Ends</span>
            <input type="datetime-local" className="field" defaultValue={lot.latest?.endAt ? toLocalInput(lot.latest.endAt) : ""}
              onBlur={(e) => e.target.value && new Date(e.target.value).toISOString() !== lot.latest?.endAt && void patch({ endAt: new Date(e.target.value).toISOString() }, "End time")} />
          </label>
          <TextDetail label="Location" placeholder="City, ST" value={lot.locationText ?? ""} onSave={(v) => patch({ locationText: v.trim() || null }, "Location")} />
          <TextDetail label="Tags, separated by commas" placeholder="Vehicles, project, parts car" value={lot.tags.join(", ")}
            onSave={(v) => patch({ tags: [...new Set(v.split(",").map((t) => t.trim()).filter(Boolean))] }, "Tags")} />
        </div>
      </details>

      <a className="text-[14px] muted underline underline-offset-2 justify-self-start" href={`/feedback?lot=${lot.id}`} target="hammer_main" onClick={() => setTimeout(() => void closeWhenSaved(), 150)}>
        Something wrong? Tell Claude
      </a>

      {/* Pinned to the bottom of the window so Done is always in reach, however much is open above. */}
      <div className="sticky bottom-0 -mx-6 px-6 py-3 bg-paper border-t border-rule flex items-center gap-2">
        <button className="btn btn-primary" onClick={() => void closeWhenSaved()}>Done</button>
        <a className="btn" href={`/lot/${lot.id}`} target="hammer_main" onClick={() => setTimeout(() => void closeWhenSaved(), 150)}>Open in Hammer</a>
        {status && <span className="text-[13px] muted ml-auto" role="status">{status}</span>}
      </div>
    </div>
  );
}

/** A field that saves when you leave it (or press Enter on a single line), only if it changed. */
function TextDetail({ label, value, onSave, placeholder, multiline, numeric }: {
  label: string; value: string; onSave: (v: string) => unknown; placeholder?: string; multiline?: boolean; numeric?: boolean;
}) {
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  const commit = () => { if (v !== value) void onSave(v); };
  const props = {
    className: `field ${numeric ? "num" : ""} ${multiline ? "min-h-[4.5rem]" : ""}`,
    value: v,
    placeholder,
    onChange: (e: { target: { value: string } }) => setV(e.target.value),
    onBlur: commit,
  };
  return (
    <label className="grid">
      <span className="label">{label}</span>
      {multiline
        ? <textarea {...props} />
        : <input {...props} inputMode={numeric ? "decimal" : undefined} onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()} />}
    </label>
  );
}

function InlineLogin({ error, onDone, onError }: { error?: string; onDone: () => void; onError: (e: string) => void }) {
  const [pass, setPass] = useState("");
  return (
    <form className="grid gap-3" onSubmit={async (e) => {
      e.preventDefault();
      try {
        await api("/login", { method: "POST", body: { passphrase: pass } });
        onDone();
      } catch {
        onError("That passphrase isn't right.");
      }
    }}>
      <p>Sign in to save this lot.</p>
      <input className="field" type="password" autoFocus autoComplete="current-password" placeholder="Passphrase" value={pass} onChange={(e) => setPass(e.target.value)} aria-label="Passphrase" />
      {error && <p className="text-red text-[14px]">{error}</p>}
      <div><button className="btn btn-primary" disabled={!pass}>Sign in and save</button></div>
    </form>
  );
}
